import {Template} from "../../../../components/template/template.jsx";
import Button from "../../../../components/button/button.jsx";
import {FaEraser, FaFileExcel, FaPlus, FaSearch} from "react-icons/fa";
import {Card} from "../../../../components/card/card.jsx";
import SelectComponent from "../../../../components/select/select-component.jsx";
import {statusArr} from "../janitorial-utils.js";
import InputForm from "../../../../components/input/input-form.jsx";
import {useQuery} from "@tanstack/react-query";
import natureService from "../../../../services/web/nature-service.js";
import {AgGridReact} from "ag-grid-react";
import aggridUtils from "../../../../utils/aggrid-utils.js";
import React, {useCallback, useMemo, useRef, useState} from "react";
import janitorialService from "../../../../services/web/janitorial-service.js";
import { DEFAULT_COL_DEF_NO_RESIZE_SUPPRESS_ALL} from "../../../../utils/ag-grid-columns-utils.js";
import {formatDateForAgGrid} from "../../../../utils/ag-grid-filters-utils.js";
import {format} from "date-fns";
import InputDateTime from "../../../../components/input/input-datetime.jsx";
import { v4 as uuidv4 } from "uuid";
import {useFormNavigation} from "../../../../hooks/use-form-navigation.js";
import {useGlobalKey} from "../../../../hooks/use-global-key.js";

const initialFilters = {
  status: "",
  requester: "",
  reason: "",
  date_start_ini: undefined,
  date_start_final: undefined,
  date_end_ini: undefined,
  date_end_final: undefined,
  address: ""
}

const pathModel = {
  create: {
    dev: `/janitorial/form`,
    isBlank: false
  },
  edit: {
    dev: `/janitorial/form`,
    isBlank: true
  }
}
const JanitorialList = () => {
  const [filters, setFilters] = useState(initialFilters);
  const gridApi = useRef(null);
  const { onRowClicked, openForm } = useFormNavigation(pathModel);

  const columnDefs = useMemo(() => [
    {field: "id", headerName: "ID", flex: 1, filter: true},
    {field: "status", headerName: "Status", flex: 2, filter: true,
      valueFormatter: params => params.value === "OPEN" ? "Aberto" : "Encerrado",
      comparator: (a, b, nodeA, nodeB, isInverted) => {
        return aggridUtils.nullLastComparator(a, b, nodeA, nodeB, isInverted);
      }},
    {field: "requester", headerName: "Solicitante", flex: 3, filter: true,
      comparator: (a, b, nodeA, nodeB, isInverted) => {
        return aggridUtils.nullLastComparator(a, b, nodeA, nodeB, isInverted);
      }},
    {field: "reason", headerName: "Natureza", flex: 3, filter: true, tooltipField: "reason",
      comparator: (a, b, nodeA, nodeB, isInverted) => {
        return aggridUtils.nullLastComparator(a, b, nodeA, nodeB, isInverted);
      }},
    {field: "date_start", headerName: "Data/hora abertura" , flex: 4,
      valueFormatter: params => params.value ? format(params.value, "dd/MM/yyyy HH:mm") : null,
      filter: "agDateColumnFilter",
      comparator: (a, b, nodeA, nodeB, isInverted) => {
        return aggridUtils.agGridSimpleDateComparator(a, b, nodeA, nodeB, isInverted);
      }
    },
    {field: "date_end", headerName: "Data/hora encerramento", flex: 4,
      valueFormatter: params => params.value ? format(params.value, "dd/MM/yyyy HH:mm") : null,
      filter: "agDateColumnFilter",
      comparator: (a, b, nodeA, nodeB, isInverted) => {
        return aggridUtils.agGridSimpleDateComparator(a, b, nodeA, nodeB, isInverted);
      }},
    {field: "description", headerName: "Descrição", hide: true},
    {field: "address", headerName: "Endereço", flex: 6, filter: "agTextColumnFilter", tooltipField: "address",
      comparator: (a, b, nodeA, nodeB, isInverted) => {
        return aggridUtils.nullLastComparator(a, b, nodeA, nodeB, isInverted);
      }},
    {field: "type_place", headerName: "Tipo de endereço", hide: true},
    {field: "street", headerName: "Logradouro", hide: true},
    {field: "number", headerName: "Número", hide: true},
    {field: "neighborhood", headerName: "Bairro", hide: true},
    {field: "crossroads", headerName: "Cruzamento", hide: true},
    {field: "reference_point", headerName: "Ponto de referência", hide: true},
    {field: "city", headerName: "Município", hide: true},
    {field: "latitude", headerName: "Latitude", hide: true},
    {field: "longitude", headerName: "Longitude", hide: true},
  ],[])

  const {data: reasons = [], isLoading: loadingReasons} = useQuery({
    queryFn: () => natureService.getJanitorialNatures(),
    queryKey: ["reasons"]
  });

  const {data: solicitations = [], isLoading: loadingSolicitations} = useQuery({
    queryFn: () => janitorialService.list(),
    queryKey: ["solicitations"]
  });

  const formattedData = useMemo(() => {
    return solicitations.map(item => ({
      ...item,
      date_start: item.date_start ? new Date(item.date_start) : null,
      date_end: item.date_end ? new Date(item.date_end) : null,
    }));
  }, [solicitations]);

  const search = useCallback(() => {
    if (!gridApi.current) return;

    const model = {};

    if (filters.status) {
      model.status = {
        filterType: "text",
        type: "contains",
        filter: filters.status,
      };
    }

    if (filters.requester) {
      model.requester = {
        filterType: "text",
        type: "contains",
        filter: filters.requester,
      };
    }

    if (filters.reason) {
      model.reason = {
        filterType: "text",
        type: "contains",
        filter: filters.reason,
      };
    }

    if (filters.address) {
      model.address = {
        filterType: "text",
        type: "contains",
        filter: filters.address,
      };
    }

    if (filters.date_start_ini || filters.date_start_final) {
      if (filters.date_start_ini && filters.date_start_final) {
        model.date_start = {
          filterType: "date",
          type: "inRange",
          dateFrom: formatDateForAgGrid(filters.date_start_ini.toISOString()),
          dateTo: formatDateForAgGrid(filters.date_start_final.toISOString()),
        };
      } else if (filters.date_start_ini) {
        model.date_start = {
          filterType: "date",
          operator: "OR",
          conditions: [
            {type: "equals", dateFrom: formatDateForAgGrid(filters.date_start_ini.toISOString())},
            {type: "greaterThan", dateFrom: formatDateForAgGrid(filters.date_start_ini.toISOString())}
          ],
        };
      } else if (filters.date_start_final) {
        model.date_start = {
          filterType: "date",
          operator: "OR",
          conditions: [
            {type: "equals", dateFrom: formatDateForAgGrid(filters.date_start_final.toISOString())},
            {type: "lessThan", dateFrom: formatDateForAgGrid(filters.date_start_final.toISOString())}
          ],
        };
      }
    }

    if (filters.date_end_ini || filters.date_end_final) {
      if (filters.date_end_ini && filters.date_end_final) {
        model.date_end = {
          filterType: "date",
          type: "inRange",
          dateFrom: formatDateForAgGrid(filters.date_end_ini.toISOString()),
          dateTo: formatDateForAgGrid(filters.date_end_final.toISOString()),
        };
      } else if (filters.date_end_ini) {
        model.date_end = {
          filterType: "date",
          operator: "OR",
          conditions: [
            {type: "equals", dateFrom: formatDateForAgGrid(filters.date_end_ini.toISOString())},
            {type: "greaterThan", dateFrom: formatDateForAgGrid(filters.date_end_ini.toISOString())}
          ],
        };
      } else if (filters.date_end_final) {
        model.date_end = {
          filterType: "date",
          operator: "OR",
          conditions: [
            {type: "equals", dateFrom: formatDateForAgGrid(filters.date_end_final.toISOString())},
            {type: "lessThan", dateFrom: formatDateForAgGrid(filters.date_end_final.toISOString())}
          ],
        };
      }
    }
    gridApi.current.setFilterModel(model);
  }, [filters]);

  const exportData = () => {
    gridApi.current.exportDataAsCsv({
      fileName: `${uuidv4()}.csv`,
      columnSeparator: ';',
      allColumns: true,
    });
  };

  const clear = () => {
    setFilters(initialFilters)
    gridApi.current.setFilterModel(null);
  }

  useGlobalKey("Enter", search);
  useGlobalKey("Escape", clear);

  const isLoading = loadingReasons || loadingSolicitations

    return (
        <Template isLoading={isLoading}>
            <Template.Header>
                <Template.Title>
                    Solicitações de zeladoria
                </Template.Title>
                <div className="flex gap-2">
                    <Button target="success" onClick={() => exportData(formattedData)}><FaFileExcel/> Exportar</Button>
                    <Button onClick={openForm}><FaPlus/> Adicionar solicitação</Button>
                </div>
            </Template.Header>
            <Template.Body>
                <Card>
                  <Card.Body>
                    <div className="grid grid-cols-12 gap-4">
                     <div className="col-span-4">
                       <SelectComponent
                         label="Status"
                         options={statusArr}
                         value={statusArr.find(item => item.value === filters.status) ?? ""}
                         onChange={(selected) => setFilters(prev => ({...prev, status: selected?.value}))}
                       />
                     </div>
                     <div className="col-span-3">
                       <InputForm
                          label="Solicitante"
                          id="requester"
                          value={filters.requester}
                          onChange={({target}) => setFilters(prev => ({...prev, requester: target.value}))}
                       />
                      </div>
                      <div className="col-span-5">
                        <SelectComponent
                          options={reasons}
                          label="Natureza"
                          value={reasons.find(item => item.value === filters.reason) ?? ""}
                          onChange={(selected) => setFilters(prev => ({...prev, reason: selected?.value}))}
                        />
                      </div>
                      <div className="col-span-4 flex h-full items-stretch">
                        <InputDateTime
                          id="dtStartIni"
                          label="Abertura entre"
                          selected={filters.date_start_ini}
                          onChange={(date) => setFilters(prev => ({ ...prev, date_start_ini: date }))}
                          dateFormat="L"
                          customIcon
                          inputClass="!rounded-e-none"
                          classNameWrapper="w-[47%]"
                        />

                        <div className="border border-[#E5E7EB] px-4 py-2 text-sm leading-none">à</div>

                        <InputDateTime
                          id="dtStartFinal"
                          selected={filters.date_start_final}
                          onChange={(date) =>
                            setFilters(prev => ({ ...prev, date_start_final: date }))
                          }
                          dateFormat="L"
                          inputClass="!rounded-s-none"
                          classNameWrapper="w-[47%]"
                        />
                      </div>
                      <div className="col-span-4 flex items-stretch">
                        <InputDateTime
                          id="dtEndStart"
                          label="Encerramento entre"
                          selected={filters.date_end_ini}
                          onChange={(date) =>
                            setFilters(prev => ({ ...prev, date_end_ini: date }))
                          }
                          dateFormat="L"
                          customIcon
                          inputClass="!rounded-e-none"
                          classNameWrapper="w-[47%]"
                        />

                        <div className="border border-[#E5E7EB] px-4 py-2 text-sm leading-none">à</div>

                        <InputDateTime
                          id="dtEndFinal"
                          selected={filters.date_end_final}
                          onChange={(date) =>
                            setFilters(prev => ({ ...prev, date_end_final: date }))
                          }
                          dateFormat="L"
                          inputClass="!rounded-s-none"
                          classNameWrapper="w-[47%]"
                        />
                      </div>
                      <div className="col-span-4 flex gap-2">
                        <div className="w-full">
                          <InputForm
                            label="Endereço"
                            id="address"
                            value={filters.address}
                            onChange={({target}) => setFilters(prev => ({...prev, address: target.value}))}
                          />
                        </div>
                        <div className="flex gap-2 justify-end">
                          <Button target="outline-primary" title="Limpar" onClick={clear}><FaEraser/></Button>
                          <Button onClick={search} title="Buscar"><FaSearch/></Button>
                        </div>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              <Card className="mt-2">
                <Card.Body>
                  <div className={"h-[calc(100vh-201px)]"}>
                    <AgGridReact
                      onGridReady={(params) => {
                        gridApi.current = params.api
                      }}
                      getRowId={(params) => String(params.data.id)}
                      rowData={formattedData}
                      columnDefs={columnDefs}
                      defaultColDef={DEFAULT_COL_DEF_NO_RESIZE_SUPPRESS_ALL}
                      animateRows={false}
                      onRowClicked={onRowClicked}
                      suppressCellFocus={true}
                      tooltipShowDelay={200}
                      onFilterChanged={aggridUtils.filterChangedNoRowsOverlay}
                      gridOptions={{localeText: aggridUtils.localization()}}
                      theme={aggridUtils.generalTheme()}
                    />
                  </div>
                </Card.Body>
              </Card>
            </Template.Body>
        </Template>
    )
}

export default JanitorialList;